/**
 * Read an image File, downscale it to fit within max dimensions (preserving
 * aspect ratio), and return a base64 data URI. PNG is used for images with
 * transparency, JPEG otherwise. Output stays small enough for inline storage
 * (typical brand logo ~10-30KB).
 */
export interface ImageToBase64Opts {
  maxWidth?: number
  maxHeight?: number
  preferFormat?: 'auto' | 'png' | 'jpeg' | 'webp'
  jpegQuality?: number
}

export async function imageFileToBase64(file: File, opts: ImageToBase64Opts = {}): Promise<string> {
  const maxW = opts.maxWidth ?? 200
  const maxH = opts.maxHeight ?? 200
  const quality = opts.jpegQuality ?? 0.9

  if (file.type === 'image/svg+xml') {
    const text = await file.text()
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(text)))
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(r.error ?? new Error('read failed'))
    r.readAsDataURL(file)
  })

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = () => reject(new Error('image decode failed'))
    i.src = dataUrl
  })

  const scale = Math.min(maxW / img.width, maxH / img.height, 1)
  const w = Math.max(1, Math.round(img.width * scale))
  const h = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.drawImage(img, 0, 0, w, h)

  const fmt = opts.preferFormat ?? 'auto'
  if (fmt === 'png' || (fmt === 'auto' && file.type === 'image/png')) {
    return canvas.toDataURL('image/png')
  }
  if (fmt === 'webp') {
    return canvas.toDataURL('image/webp', quality)
  }
  return canvas.toDataURL('image/jpeg', quality)
}
